/**
 * SlicerVersionField Component
 *
 * Displays slicer version with fallback behavior:
 * 1. First checks the Printing record's slicerVersion field
 * 2. Falls back to fetching print-start event and parsing rawContent.fields.slicerVersion
 *
 * Mimics hardware-digester behavior with React Admin 5.x and Material-UI v5.
 */

import { useState, useEffect } from 'react';
import { InputLabel, LinearProgress } from '@mui/material';
import { useDataProvider, useRecordContext } from 'react-admin';
import { Printing, EventType, Event } from '../../../types';

interface PrintStartEventContent {
  fields?: {
    slicerVersion?: string;
  };
}

export const SlicerVersionField = () => {
  const record = useRecordContext<Printing>();
  const provider = useDataProvider();
  const [slicerVersion, setSlicerVersion] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchPrintStartEvent = async () => {
      if (!record?.id) {
        setLoading(false);
        return;
      }

      // Check record first (same as hardware-digester)
      if (record.slicerVersion) {
        setSlicerVersion(record.slicerVersion);
        setLoading(false);
        return;
      }

      try {
        // Fetch print-start event type
        const { data: eventTypes } = await provider.getList<EventType>('eventTypes', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'name', order: 'ASC' },
          filter: { name: 'print-start' },
        });

        if (!eventTypes?.[0]?.id) {
          setLoading(false);
          return;
        }

        // Fetch print-start event for this printing
        const { data: events } = await provider.getList<Event>('events', {
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'eventTime', order: 'ASC' },
          filter: {
            eventTypeId: eventTypes[0].id,
            printingId: record.id,
          },
        });

        if (events?.[0]?.rawContent) {
          try {
            const content: PrintStartEventContent = JSON.parse(events[0].rawContent);
            setSlicerVersion(content?.fields?.slicerVersion || '');
          } catch {
            // Silently fail if rawContent is not valid JSON
          }
        }
      } catch {
        // Error fetching slicer version
      } finally {
        setLoading(false);
      }
    };

    fetchPrintStartEvent();
  }, [provider, record?.id, record?.slicerVersion]);

  return (
    <>
      <InputLabel shrink={true} sx={{ margin: '8px 0 4px 0' }}>
        <span>Slicer version</span>
      </InputLabel>
      {loading ? (
        <div style={{ width: 60 }}>
          <LinearProgress />
        </div>
      ) : (
        <span>{slicerVersion || 'N/A'}</span>
      )}
    </>
  );
};

SlicerVersionField.defaultProps = {
  label: 'Slicer version',
};
